import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormLabel,
  Grid,
  IconButton,
  Input,
  List,
  ListItem,
  ListItemButton,
  ListItemContent,
  Modal,
  ModalClose,
  Sheet,
  Skeleton,
  Stack,
  Tab,
  TabList,
  Tabs,
  TabPanel,
  Typography,
} from '@mui/joy';
import GroupIcon from '@mui/icons-material/Group';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useTheme } from '@mui/joy/styles';
import {
  listAdminAllDevotees,
  fetchAdminDevoteeSadhanaCard,
  fetchAdminDevoteeSadhanaCards,
  type AdminAllDevoteesResponse,
} from '../../api';
import AdminWeeklySadhanaCardView from '../../components/sadhana/AdminWeeklySadhanaCardView';
import { useSnackbarStore } from '../../store/useSnackbarStore';
import { getWeekStartDate, formatDateToYYYYMMDD, addWeeks } from '../../utils/date';

interface DevoteeInfo {
  memberId: number;
  name: string;
  email?: string;
  status?: string;
}

const AdminSadhanaPage: React.FC = () => {
  const { showSnackbar } = useSnackbarStore();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const heroBackground = isDark
    ? 'linear-gradient(180deg, rgba(15, 23, 42, 0.96) 0%, rgba(10, 15, 26, 0.98) 100%)'
    : 'radial-gradient(120% 140% at 10% 0%, rgba(255, 214, 153, 0.18) 0%, rgba(255, 214, 153, 0) 55%), radial-gradient(120% 140% at 100% 10%, rgba(160, 196, 255, 0.2) 0%, rgba(160, 196, 255, 0) 60%), linear-gradient(180deg, rgba(255, 252, 245, 1) 0%, rgba(250, 251, 255, 1) 40%, rgba(246, 247, 252, 1) 100%)';
  const heroOverlay = isDark
    ? 'radial-gradient(120% 120% at 20% -10%, rgba(56, 189, 248, 0.14), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(251, 191, 36, 0.12), transparent 55%)'
    : 'radial-gradient(120% 120% at 20% -10%, rgba(255, 220, 160, 0.45), transparent 60%), radial-gradient(120% 120% at 100% 0%, rgba(129, 158, 255, 0.28), transparent 55%)';
  const heroTitle = isDark ? 'rgba(248,250,252,0.94)' : 'text.primary';
  const heroBody = isDark ? 'rgba(226,232,240,0.8)' : 'text.secondary';
  const panelWarm = isDark
    ? 'linear-gradient(135deg, rgba(30, 41, 59, 0.9), rgba(15, 23, 42, 0.95))'
    : 'linear-gradient(135deg, rgba(255, 237, 213, 0.9), rgba(255, 247, 231, 0.9))';
  const panelCool = isDark
    ? 'linear-gradient(135deg, rgba(15, 23, 42, 0.9), rgba(30, 41, 59, 0.95))'
    : 'linear-gradient(135deg, rgba(224, 243, 255, 0.8), rgba(241, 249, 255, 0.95))';
  const orbColor = isDark ? 'rgba(56, 189, 248, 0.18)' : 'rgba(255, 214, 153, 0.35)';

  const [activeTab, setActiveTab] = useState<number>(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDevoteeId, setSelectedDevoteeId] = useState<number | null>(null);
  const [selectedDevoteeName, setSelectedDevoteeName] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedWeekStartDate, setSelectedWeekStartDate] = useState<string | null>(null);

  const [allDevotees, setAllDevotees] = useState<DevoteeInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [devoteeSubmissionMap, setDevoteeSubmissionMap] = useState<{ [key: number]: { hasSubmission: boolean; lastSubmission?: string } }>({});

  const [submittedWeeks, setSubmittedWeeks] = useState<any[]>([]);
  const [selectedCard, setSelectedCard] = useState<any | null>(null);
  const [loadingCard, setLoadingCard] = useState(false);

  // Week view state
  const [allCards, setAllCards] = useState<any[]>([]);
  const [weekViewOffset, setWeekViewOffset] = useState<number>(0);
  const weekViewDate = addWeeks(getWeekStartDate(new Date()), weekViewOffset);
  const weekViewDateStr = formatDateToYYYYMMDD(weekViewDate);
  const weekLabel = new Date(weekViewDateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  const filteredDevotees = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return allDevotees;
    return allDevotees.filter((d) =>
      d.name.toLowerCase().includes(term) ||
      (d.email && d.email.toLowerCase().includes(term)) ||
      String(d.memberId).includes(term),
    );
  }, [allDevotees, searchTerm]);

  const sadhanaStats = useMemo(() => {
    const total = allDevotees.length;
    const active = Object.values(devoteeSubmissionMap).filter((item) => item.hasSubmission).length;
    const inactive = Math.max(total - active, 0);
    return { total, active, inactive };
  }, [allDevotees, devoteeSubmissionMap]);

  const loadDevotees = async () => {
    try {
      setLoading(true);
      const result: AdminAllDevoteesResponse = await listAdminAllDevotees();
      setAllDevotees(result.devotees || []);

      // Check submission status for each devotee
      const submissionMap: { [key: number]: { hasSubmission: boolean; lastSubmission?: string } } = {};
      for (const devotee of result.devotees || []) {
        try {
          const weeks = await fetchAdminDevoteeSadhanaCards(devotee.memberId, 1, 0);
          const weeksList = (weeks as any).cards || (weeks as any).weeks || [];
          const hasSubmissions = weeksList.length > 0;
          let lastSubmission: string | undefined;

          if (hasSubmissions && weeksList[0]?.submittedAt) {
            const date = new Date(weeksList[0].submittedAt);
            const day = date.getDate();
            const month = date.toLocaleString('en-US', { month: 'short' });
            lastSubmission = `${day} ${month}`;
          }

          submissionMap[devotee.memberId] = {
            hasSubmission: hasSubmissions,
            lastSubmission,
          };
        } catch (error) {
          submissionMap[devotee.memberId] = { hasSubmission: false };
        }
      }
      setDevoteeSubmissionMap(submissionMap);
    } catch (error) {
      console.error('Failed to fetch devotees:', error);
      setAllDevotees([]);
      showSnackbar('Failed to fetch devotees', 'danger');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDevotees();
  }, []);

  useEffect(() => {
    const fetchWeeks = async () => {
      if (!selectedDevoteeId || !isModalOpen) {
        setSubmittedWeeks([]);
        return;
      }

      try {
        const result = await fetchAdminDevoteeSadhanaCards(selectedDevoteeId, 52, 0);
        const allWeeks = (result as any).cards || (result as any).weeks || [];
        const weeks = allWeeks.filter((w: any) => w.isSubmitted || (w.submittedAt && w.submittedAt !== null));
        setSubmittedWeeks(weeks);

        if (weeks.length > 0 && !selectedWeekStartDate) {
          const firstWeek = weeks[0];
          setSelectedWeekStartDate(firstWeek.weekStartDate?.split('T')[0] || null);
        }
      } catch (error) {
        console.error('Failed to fetch weeks:', error);
        setSubmittedWeeks([]);
        showSnackbar('Failed to fetch sadhana weeks', 'danger');
      }
    };

    fetchWeeks();
  }, [isModalOpen, selectedDevoteeId, showSnackbar]);

  useEffect(() => {
    const fetchCard = async () => {
      if (!selectedDevoteeId || !selectedWeekStartDate) {
        setSelectedCard(null);
        return;
      }

      try {
        setLoadingCard(true);
        const card = await fetchAdminDevoteeSadhanaCard(selectedDevoteeId, selectedWeekStartDate);
        setSelectedCard(card);
      } catch (error) {
        console.error('Failed to fetch weekly card:', error);
        setSelectedCard(null);
        showSnackbar('Failed to fetch weekly card', 'danger');
      } finally {
        setLoadingCard(false);
      }
    };

    fetchCard();
  }, [selectedDevoteeId, selectedWeekStartDate, showSnackbar]);

  // Fetch all devotees' cards for the week view
  useEffect(() => {
    const fetchWeekCards = async () => {
      if (activeTab !== 1) {
        setAllCards([]);
        return;
      }

      try {
        setLoading(true);
        const cards: any[] = [];

        for (const devotee of allDevotees) {
          try {
            const card = await fetchAdminDevoteeSadhanaCard(devotee.memberId, weekViewDateStr);
            if (card && card.isSubmitted) {
              cards.push({
                ...card,
                devoteeName: devotee.name,
                email: devotee.email,
                memberId: devotee.memberId,
              });
            }
          } catch (error) {
            // Card not found for this week, skip
            console.log(`No card for devotee ${devotee.memberId} in week ${weekViewDateStr}`);
          }
        }

        setAllCards(cards);
      } catch (error) {
        console.error('Failed to fetch week view cards:', error);
        setAllCards([]);
        showSnackbar('Failed to fetch week view cards', 'danger');
      } finally {
        setLoading(false);
      }
    };

    fetchWeekCards();
  }, [activeTab, weekViewDateStr, allDevotees, showSnackbar]);

  const handleOpenModal = (devotee: DevoteeInfo) => {
    setSelectedDevoteeId(devotee.memberId);
    setSelectedDevoteeName(devotee.name);
    setSelectedWeekStartDate(null);
    setSelectedCard(null);
    setSubmittedWeeks([]);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedWeekStartDate(null);
    setSelectedCard(null);
    setSubmittedWeeks([]);
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'hidden', maxWidth: '100%' }}>
      <Stack spacing={3} sx={{ width: '100%', maxWidth: '100%', px: { xs: 1.5, sm: 2, md: 0 } }}>
        <Card
          variant="outlined"
          sx={{
            position: 'relative',
            overflow: 'hidden',
            borderRadius: 'xl',
            borderColor: 'divider',
            boxShadow: 'lg',
            background: heroBackground,
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              background: heroOverlay,
              opacity: 0.9,
              pointerEvents: 'none',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              width: 180,
              height: 180,
              borderRadius: '50%',
              right: { xs: -60, md: -30 },
              top: { xs: 40, md: 20 },
              background: orbColor,
              filter: 'blur(6px)',
              animation: 'sadhana-float 8s ease-in-out infinite',
              '@keyframes sadhana-float': {
                '0%': { transform: 'translateY(0px)' },
                '50%': { transform: 'translateY(-12px)' },
                '100%': { transform: 'translateY(0px)' },
              },
              pointerEvents: 'none',
            }}
          />
          <CardContent sx={{ position: 'relative' }}>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ xs: 'flex-start', md: 'center' }} justifyContent="space-between" spacing={2} sx={{ minWidth: 0 }}>
                <Stack spacing={0.75}>
                  <Typography
                    level="h2"
                    sx={{
                      fontWeight: 700,
                      letterSpacing: '-0.02em',
                      color: heroTitle,
                    }}
                  >
                    Admin Sadhana Dashboard
                  </Typography>
                  <Typography level="body-sm" sx={{ color: heroBody, maxWidth: 520 }}>
                    View and review weekly sadhana submissions for all devotees while keeping a pulse on community engagement.
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0 }}>
                  <Chip variant="soft" color="primary">
                    {sadhanaStats.total} devotees
                  </Chip>
                  <Chip variant="soft" color="success">
                    {sadhanaStats.active} active
                  </Chip>
                  <Chip variant="soft" color="neutral">
                    {sadhanaStats.inactive} quiet
                  </Chip>
                  <Button startDecorator={<RefreshIcon />} variant="soft" onClick={loadDevotees} size="sm">
                    Refresh
                  </Button>
                </Stack>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ minWidth: 0 }}>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelWarm,
                    boxShadow: 'sm',
                    minWidth: 0,
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Week focus
                    </Typography>
                    <Typography level="title-lg" sx={{ mt: 0.5 }}>
                      {weekLabel}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Review submissions and mentor notes for this cycle.
                    </Typography>
                  </CardContent>
                </Card>
                <Card
                  variant="soft"
                  sx={{
                    flex: 1,
                    borderRadius: 'lg',
                    background: panelCool,
                    boxShadow: 'sm',
                    minWidth: 0,
                  }}
                >
                  <CardContent>
                    <Typography level="title-sm" sx={{ color: 'text.secondary' }}>
                      Weekly cards loaded
                    </Typography>
                    <Typography level="title-lg" sx={{ mt: 0.5 }}>
                      {activeTab === 1 ? allCards.length : submittedWeeks.length}
                    </Typography>
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      {activeTab === 1 ? 'Submitted cards for the selected week.' : 'Submitted weeks for the selected devotee.'}
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Tabs
          value={activeTab}
          onChange={(event, newValue) => setActiveTab(newValue as number)}
          sx={{
            borderRadius: 'lg',
            backgroundColor: 'background.surface',
            boxShadow: 'sm',
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <TabList
            sx={{
              px: 1,
              py: 1,
              gap: 1,
              bgcolor: 'transparent',
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Tab sx={{ borderRadius: 'full' }}>Devotees</Tab>
            <Tab sx={{ borderRadius: 'full' }}>Week View</Tab>
          </TabList>

          <TabPanel value={0}>
            <CardContent>
              <Stack spacing={2}>
                <FormControl>
                  <FormLabel>Search devotees</FormLabel>
                  <Input
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, email, or member id"
                    startDecorator={<SearchRoundedIcon />}
                    sx={{
                      bgcolor: 'background.surface',
                      boxShadow: 'sm',
                      borderRadius: 'lg',
                    }}
                  />
                </FormControl>

                {loading ? (
                  <Stack spacing={1.5}>
                    {[...Array(4)].map((_, idx) => (
                      <Skeleton key={idx} height={72} variant="rectangular" />
                    ))}
                  </Stack>
                ) : filteredDevotees.length === 0 ? (
                  <Alert color="warning" startDecorator={<GroupIcon />}>
                    No devotees found. Try adjusting your search.
                  </Alert>
                ) : (
                  <List
                    variant="outlined"
                    sx={{
                      borderRadius: 'lg',
                      borderColor: 'divider',
                      boxShadow: 'sm',
                      overflow: 'hidden',
                    }}
                  >
                    {filteredDevotees.map((devotee) => {
                      const status = devoteeSubmissionMap[devotee.memberId];
                      return (
                        <ListItem
                          key={devotee.memberId}
                          sx={{
                            alignItems: 'center',
                            gap: 2,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            '&:last-child': { borderBottom: 'none' },
                            bgcolor: 'background.surface',
                            transition: 'transform 0.2s, box-shadow 0.2s',
                            '&:hover': {
                              transform: 'translateY(-1px)',
                              boxShadow: 'sm',
                            },
                          }}
                        >
                          <ListItemContent>
                            <Stack spacing={0.5}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Typography level="title-sm">{devotee.name}</Typography>
                                <Chip size="sm" variant="soft">
                                  #{devotee.memberId}
                                </Chip>
                                <Chip size="sm" variant="soft" color={status?.hasSubmission ? 'success' : 'neutral'}>
                                  {status?.hasSubmission ? 'Active' : 'Inactive'}
                                </Chip>
                                {status?.lastSubmission && (
                                  <Chip size="sm" variant="soft" color="primary">
                                    Last: {status.lastSubmission}
                                  </Chip>
                                )}
                              </Stack>
                              {devotee.email && (
                                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                  {devotee.email}
                                </Typography>
                              )}
                              <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                {status?.lastSubmission ? 'Recent engagement logged.' : 'No submissions yet.'}
                              </Typography>
                            </Stack>
                          </ListItemContent>
                          <IconButton
                            size="sm"
                            variant="soft"
                            color="primary"
                            onClick={() => handleOpenModal(devotee)}
                          >
                            <GroupIcon />
                          </IconButton>
                        </ListItem>
                      );
                    })}
                  </List>
                )}
              </Stack>
            </CardContent>
          </TabPanel>

          <TabPanel value={1}>
            <Stack spacing={3}>
              <Card variant="outlined" sx={{ borderRadius: 'lg', borderColor: 'divider', boxShadow: 'sm' }}>
                <CardContent>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconButton size="sm" variant="soft" onClick={() => setWeekViewOffset((prev) => prev - 1)}>
                          <ChevronLeftIcon />
                        </IconButton>
                        <Stack spacing={0.25}>
                          <Typography level="title-md">
                            Week: {weekViewDateStr}
                          </Typography>
                          <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                            {weekLabel}
                          </Typography>
                        </Stack>
                        <IconButton
                          size="sm"
                          variant="soft"
                          onClick={() => setWeekViewOffset((prev) => prev + 1)}
                          disabled={weekViewOffset >= 0}
                        >
                          <ChevronRightIcon />
                        </IconButton>
                      </Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Chip variant="soft" color="primary">
                          {allCards.length} submissions
                        </Chip>
                        {weekViewOffset !== 0 && (
                          <Button size="sm" variant="outlined" onClick={() => setWeekViewOffset(0)}>
                            Current Week
                          </Button>
                        )}
                      </Stack>
                    </Box>

                    {loading ? (
                      <Stack spacing={2}>
                        {[...Array(4)].map((_, idx) => (
                          <Skeleton key={idx} height={200} variant="rectangular" />
                        ))}
                      </Stack>
                    ) : allCards.length === 0 ? (
                      <Alert color="neutral">No sadhana cards available</Alert>
                    ) : (
                      <Grid container spacing={2}>
                        {allCards.map((card: any, idx: number) => (
                          <Grid xs={12} sm={6} md={4} key={idx}>
                            <Card
                              variant="outlined"
                              sx={{
                                cursor: 'pointer',
                                transition: 'all 0.2s',
                                borderRadius: 'lg',
                                borderColor: 'divider',
                                '&:hover': {
                                  boxShadow: 'lg',
                                  borderColor: 'primary.outlinedBorder',
                                  transform: 'translateY(-2px)',
                                },
                              }}
                              onClick={() => {
                                setSelectedDevoteeId(card.memberId);
                                setSelectedDevoteeName(card.devoteeName || `Devotee #${card.memberId}`);
                                setSelectedWeekStartDate(card.weekStartDate?.split('T')[0] || null);
                                setIsModalOpen(true);
                              }}
                            >
                              <CardContent>
                                <Stack spacing={1}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                                    <Typography level="h4">{card.devoteeName || `Devotee #${card.memberId}`}</Typography>
                                    <Chip size="sm" color="success" variant="soft">
                                      ✓ Submitted
                                    </Chip>
                                  </Box>
                                  {card.email && (
                                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                      {card.email}
                                    </Typography>
                                  )}
                                  <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                    {card.weekStartDate?.split('T')[0]} — {card.weekEndDate?.split('T')[0]}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                                    <Box>
                                      <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                        Total Points
                                      </Typography>
                                      <Typography level="title-lg">{card.grandTotal || card.totalPoints || 0}</Typography>
                                    </Box>
                                  </Box>
                                </Stack>
                              </CardContent>
                            </Card>
                          </Grid>
                        ))}
                      </Grid>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Stack>
          </TabPanel>
        </Tabs>

        <Modal
          aria-labelledby="sadhana-modal-title"
          open={isModalOpen}
          onClose={handleCloseModal}
          sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 99999 }}
          slotProps={{ backdrop: { sx: { zIndex: 99998 } } }}
        >
          <Sheet
            variant="outlined"
            sx={{
              width: 'min(1200px, 100vw - 64px)',
              maxWidth: 1200,
              minWidth: 0,
              borderRadius: 'lg',
              p: 3,
              boxShadow: 'lg',
              maxHeight: '90vh',
              overflowY: 'auto',
              position: 'relative',
              zIndex: 99999,
              background:
                'radial-gradient(120% 120% at 10% 0%, rgba(255, 232, 190, 0.18), transparent 55%), radial-gradient(120% 120% at 100% 0%, rgba(175, 205, 255, 0.18), transparent 60%), #ffffff',
            }}
          >
            <ModalClose variant="plain" sx={{ m: 1 }} />

            {selectedDevoteeId && (
              <Stack spacing={3}>
                <Box>
                  <Typography id="sadhana-modal-title" level="h2">
                    {selectedDevoteeName}'s Sadhana
                  </Typography>
                  {selectedWeekStartDate && (
                    <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
                      Week of {selectedWeekStartDate}
                    </Typography>
                  )}
                </Box>

                <Grid container spacing={3}>
                  <Grid xs={12} md={3}>
                    <Typography level="title-md" sx={{ mb: 2 }}>
                      Submitted Weeks
                    </Typography>
                    {submittedWeeks.length === 0 ? (
                      <Alert color="warning" size="sm">
                        <Typography level="body-sm">No submissions yet.</Typography>
                      </Alert>
                    ) : (
                      <List
                        variant="outlined"
                        sx={{
                          borderRadius: 'md',
                          maxHeight: '60vh',
                          overflowY: 'auto',
                          '--ListItem-paddingY': '0.5rem',
                        }}
                      >
                        {submittedWeeks.map((week: any) => {
                          const ws = week.weekStartDate?.split('T')[0] || '';
                          const we = week.weekEndDate?.split('T')[0] || '';
                          const isActive = selectedWeekStartDate === ws;
                          return (
                            <ListItem key={ws}>
                              <ListItemButton selected={isActive} onClick={() => setSelectedWeekStartDate(ws)}>
                                <ListItemContent>
                                  <Stack spacing={0.5}>
                                    <Typography level="body-sm" fontWeight={isActive ? 600 : 400}>
                                      {ws}
                                    </Typography>
                                    <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                                      to {we}
                                    </Typography>
                                  </Stack>
                                </ListItemContent>
                                {isActive && (
                                  <Chip size="sm" color="primary" variant="soft">
                                    Viewing
                                  </Chip>
                                )}
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    )}
                  </Grid>

                  <Grid xs={12} md={9}>
                    {loadingCard && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                        <CircularProgress size="md" />
                      </Box>
                    )}

                    {!loadingCard && selectedCard && <AdminWeeklySadhanaCardView card={selectedCard} />}

                    {!loadingCard && !selectedCard && (
                      <Alert color="warning">
                        <Typography level="body-sm">
                          Select a submitted week from the list to view the full card.
                        </Typography>
                      </Alert>
                    )}
                  </Grid>
                </Grid>
              </Stack>
            )}
          </Sheet>
        </Modal>
      </Stack>
    </Box>
  );
};

export default AdminSadhanaPage;
